import React from 'react';
import { useFlowObserverPanelStore } from '@flow-studio/flow-observer-core';
import { formatTime } from '../../utils/dates.js';
import type { FlowObserverTabContentProps, ExecutionLogEntry } from '@flow-studio/flow-observer-core';

export const LogDetailTabContent: React.FC<FlowObserverTabContentProps> = ({
  tabId,
  loadState,
  error,
  onRetry,
}) => {
  const dataBuffer = useFlowObserverPanelStore(
    (s) => s.tabRuntimeStates[tabId]?.dataBuffer ?? []
  );

  if (loadState === 'loading') return <div className="fop-loading">Loading…</div>;

  if (loadState === 'error' && error) {
    return (
      <div className="fop-error" role="alert">
        <span>{error.message}</span>
        {error.retryable && (
          <button className="fop-error__retry" onClick={onRetry}>Retry</button>
        )}
      </div>
    );
  }

  const entry = dataBuffer.length > 0
    ? (dataBuffer[dataBuffer.length - 1] as ExecutionLogEntry)
    : null;

  if (!entry) {
    return (
      <div className="fop-empty">
        <span>Click a log entry to view its details</span>
      </div>
    );
  }

  return (
    <div className="fop-log-detail">
      <div className="fop-log-detail__header">
        <span className={`fop-log-level fop-log-level--${entry.level} fop-log-detail__badge`}>
          {entry.level.toUpperCase()}
        </span>
        <span className="fop-log-detail__time">
          {new Date(entry.timestamp).toLocaleTimeString()}
        </span>
      </div>

      <div className="fop-log-detail__body">
        <div className="fop-log-detail__field">
          <span className="fop-log-detail__label">Message</span>
          <span className="fop-log-detail__value">{entry.message}</span>
        </div>

        {entry.sourceNodeLabel && (
          <div className="fop-log-detail__field">
            <span className="fop-log-detail__label">Node</span>
            <span className="fop-log-detail__value">{entry.sourceNodeLabel}</span>
          </div>
        )}

        {entry.sourceNodeId && (
          <div className="fop-log-detail__field">
            <span className="fop-log-detail__label">Node ID</span>
            <span className="fop-log-detail__value fop-log-detail__value--mono">
              {entry.sourceNodeId}
            </span>
          </div>
        )}

        <div className="fop-log-detail__field">
          <span className="fop-log-detail__label">Timestamp</span>
          <span className="fop-log-detail__value fop-log-detail__value--mono">
            {entry.timestamp}
          </span>
        </div>

        <div className="fop-log-detail__field">
          <span className="fop-log-detail__label">Entry ID</span>
          <span className="fop-log-detail__value fop-log-detail__value--mono">
            {entry.id}
          </span>
        </div>

        {entry.details && (
          <div className="fop-log-detail__field fop-log-detail__field--full">
            <span className="fop-log-detail__label">Details</span>
            <pre className="fop-log-detail__pre">{entry.details}</pre>
          </div>
        )}
      </div>
    </div>
  );
};
